import * as couponService from '@cart-services/core/couponService'
import { useFpB2bCouponService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-coupon-service'
import { type MergedCouponCriterion, type RegularGiveawayValue } from '@cart-services/types'
import { type CartFormValues, CART_FORM_ERROR_MESSAGE } from '@cart-services/zod-schema/cartFormSchema'
import { RegularGiveawaySelect } from '@cart-ui/RegularGiveawaySelect/RegularGiveawaySelect'
import { fieldRefHelper } from '@utils/fieldRefHelper'
import { useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useCart } from '../CartProvider/CartProvider'

export interface RegularGiveawaySelectControllerProps {
  index: number
  criterion: MergedCouponCriterion
}

export const RegularGiveawaySelectController = (props: RegularGiveawaySelectControllerProps) => {
  const { index, criterion } = props
  const { projectCode, currency, campaignId, countryCode, campaignCodeFromUrl, platform } = useCart()
  const { control, setValue } = useFormContext<CartFormValues>()
  const { updateSelectedRegularGiveaways, selectedCouponGiveawayMap } = useFpB2bCouponService(
    campaignId,
    projectCode,
    countryCode,
    campaignCodeFromUrl,
    platform,
  )

  const {
    couponCriterionId,
    displayName,
    isAllUnavailable,
    isApplied,
    amount,
    value,
    moreAmount,
    moreQuantity,
    count,
    options,
    disabled,
    selectedRegularGiveawayStock,
  } = useMemo(() => couponService.getRegularGiveawaySelectProps(criterion, selectedCouponGiveawayMap), [criterion, selectedCouponGiveawayMap])

  if (amount === null) return null

  return (
    <Controller
      control={control}
      name={`regularGiveaways.${index}`}
      render={({ field: { ref }, fieldState: { error } }) => {
        const isStockNotEnoughError = error?.message === CART_FORM_ERROR_MESSAGE.NOT_ENOUGH_GIVEAWAY
        return (
          <RegularGiveawaySelect
            rootRef={fieldRefHelper(ref)}
            displayName={displayName}
            isApplied={isApplied}
            currency={currency}
            moreAmount={moreAmount}
            moreQuantity={moreQuantity}
            count={count}
            options={options}
            amount={amount}
            disabled={disabled}
            isAllUnavailable={isAllUnavailable}
            onChange={([productId, projectCode, sku]) => {
              const regularGiveawayValue: RegularGiveawayValue = {
                isAllUnavailable,
                isApplied,
                product_id: productId,
                sku,
                project_code: projectCode,
                couponCriterionId,
                selectedRegularGiveawayStock,
              }
              setValue(`regularGiveaways.${index}`, regularGiveawayValue, { shouldValidate: true })
              if (productId && projectCode) {
                updateSelectedRegularGiveaways(couponCriterionId, {
                  product_id: productId,
                  project_code: projectCode,
                  sku,
                })
              }
            }}
            value={value?.sku ? [value.product_id, value.project_code, value.sku] : []}
            errorHighlight={isStockNotEnoughError}
            error={!!error?.message}
            helpText={error?.message}
          />
        )
      }}
    />
  )
}
